import { InfaceAuthError } from '@inface-sdk/web-auth'
import { STEP_STATUS } from './utils'
import { secondsToDhms } from '../../utils/time'
import { ERROR_CODES, ERROR_NAMES } from './constants'

export default class AccountStatusHandler {
  /**
   * @param {object} deps
   * @param {object} deps.modalService
   * @param {object} deps.navigate
   * @param {object} deps.gameAuthService
   * @param {string} deps.securityUrl
   * @param {string} deps.gameRestrictPath
   * @param {Function} deps.translate
   * @param {string} deps.gid
   */
  constructor({
    modalService,
    navigate,
    gameAuthService,
    securityUrl,
    gameRestrictPath,
    translate,
    gid,
  }) {
    this.modalService = modalService
    this.navigate = navigate
    this.gameAuthService = gameAuthService
    this.securityUrl = securityUrl
    this.gameRestrictPath = gameRestrictPath
    this.$t = translate
    this.gid = gid
  }

  /**
   * - 게임탈퇴 유예 복구: 계정 복구 API 호출 후 성공 시 처음부터 플로우 다시 시작
   * - 그 외: 에러 코드 전송 후 플로우 중단
   */
  async handleError(error = {}, { webToken } = {}) {
    const handleErrorResponse = {
      status: STEP_STATUS.STOP,
      payload: {},
    }

    const { errorCode, errorText, result } = error.rawResponseData || {}

    let message = errorText || this.$t('error_modal.message')
    const errorResponse = new InfaceAuthError({
      code: ERROR_CODES.FAIL_TO_GAME_TOKEN_ROAMING_FOR_UNEXPECTED_ERROR,
      name: ERROR_NAMES[ERROR_CODES.FAIL_TO_GAME_TOKEN_ROAMING_FOR_UNEXPECTED_ERROR],
      message,
      data: error,
    })

    switch (errorCode) {
      case 1202: // GUID 존재
        throw errorResponse
      case 1205: // GUID 휴면
        const { memberId, memberType, npaCode } = result
        await this.handleReactivate({
          errorText, memberId, memberType, npaCode,
        })
        handleErrorResponse.status = STEP_STATUS.RESTART // 처음부터 플로우 다시 시작
        return handleErrorResponse
      case 1206: // NSRRS - 게임실행제한 (본인인증제한)
        await this.handleGameRestrict({ errorText })
        return handleErrorResponse
      case 1301: // 게임탈퇴 유예
        await this.handleRecover({
          withdrawExpiresIn: result.withdrawExpiresIn,
          webToken,
        })
        handleErrorResponse.status = STEP_STATUS.RESTART // 처음부터 플로우 다시 시작
        return handleErrorResponse
      default:
        message = this.$t('error_modal.message')
    }

    if (errorCode) {
      message += `(${errorCode})`
    }

    throw errorResponse
  }

  /**
   * 휴면 해제 안내
   * - 휴면 해제 클릭 시, 휴면 해제 API 호출 후 성공 시 처음부터 플로우 다시 시작
   * - 취소 클릭 시, 모바일 앱으로 에러 코드 전송
   */
  async handleReactivate({
    memberId, memberType, npaCode, errorText,
  }) {
    const message = this.$t('reactivate_modal.npres_reactivate_account_dialog_description') || errorText
    const confirmText = this.$t('reactivate_modal.npres_reactivate_account_dialog_positive_button_text')

    const action = await this.modalService.confirm({ message, confirmText })
    if (action !== 'confirm') {
      throw new InfaceAuthError({
        code: ERROR_CODES.FAIL_TO_GAME_TOKEN_ROAMING_FOR_CANCELLING_REACTIVATION,
        name: ERROR_NAMES[ERROR_CODES.FAIL_TO_GAME_TOKEN_ROAMING_FOR_CANCELLING_REACTIVATION],
        message,
        data: {
          captureError: false,
        },
      })
    }

    const { success, error } = await this.gameAuthService.insignGameAuth.reactivateAccountWithNpaCode({
      memberId, memberType, npaCode,
    })
    if (!success) {
      throw error
    }

    await this.modalService.warning({
      message: this.$t('reactivate_modal.npres_reactivate_account_success_text'),
    })
  }

  /**
   * 본인인증 제한 해지 안내
   * - 본인 확인 클릭 시, 보안센터 게임실행제한 페이지로 이동
   * - 취소 클릭 시, 모바일 앱으로 에러 코드 전송
   */
  async handleGameRestrict({ errorText }) {
    const confirmText = this.$t('restrict_modal.confirm')
    const action = await this.modalService.confirm({ message: errorText, confirmText })

    if (action !== 'confirm') {
      throw new InfaceAuthError({
        code: ERROR_CODES.FAIL_TO_GAME_TOKEN_ROAMING_FOR_CANCELLING_RELEASE_NSRRS_BY_IDENTITY_VERIFICATION,
        name: ERROR_NAMES[ERROR_CODES.FAIL_TO_GAME_TOKEN_ROAMING_FOR_CANCELLING_RELEASE_NSRRS_BY_IDENTITY_VERIFICATION],
        message: errorText,
        data: {
          captureError: false,
        },
      })
    }

    this.navigate(`${this.securityUrl}${this.gameRestrictPath}?gameCode=${this.gid}`)
  }

  /**
   * 게임탈퇴 복구 안내
   * - 복구 클릭 시, 계정 복구 API 호출 후 성공 시 처음부터 플로우 다시 시작
   * - 취소 클릭 시, 모바일 앱으로 에러 코드 전송
   */
  async handleRecover({ withdrawExpiresIn, webToken }) {
    const { days, hours, minutes } = secondsToDhms(withdrawExpiresIn)
    const msg1 = this.$t('recover_modal.message1', { D: days, HH: hours, mm: minutes })
    const msg2 = this.$t('recover_modal.message2')
    const action = await this.modalService.confirm({ message: `${msg1}\n${msg2}`, confirmText: this.$t('recover_modal.confirm') })

    if (action !== 'confirm') {
      await this.modalService.warning({
        message: `${this.$t('recover_cancel_modal.message1')}\n${this.$t('recover_cancel_modal.message2')}`,
      })
      throw new InfaceAuthError({
        code: ERROR_CODES.FAIL_TO_GAME_TOKEN_ROAMING_FOR_CANCELLING_RECOVERY,
        name: ERROR_NAMES[ERROR_CODES.FAIL_TO_GAME_TOKEN_ROAMING_FOR_CANCELLING_RECOVERY],
        message: this.$t('recover_cancel_modal.message1'),
        data: {
          captureError: false,
        },
      })
    }

    // 게임탈퇴 복구 처리 진행
    const ticket = await this.#getWebTicketByWebToken({ webToken })
    await this.gameAuthService.recoverUser(ticket)
  }

  /**
   * 웹 토큰을 사용하여 티켓을 발급하며, 발급된 티켓은 아래 과정에서 사용됩니다.
   * - 게임탈퇴 복구 API 호출
   * - 게임탈퇴 복구 시 gid는 대상 게임 gid로 설정합니다.
   */
  async #getWebTicketByWebToken({ webToken }) {
    const { success, data, error } = await this.gameAuthService.insignGameAuth.issueIasTicketByWebToken({ webToken, gid: this.gid })
    const { ticket } = data

    if (!success) {
      throw error
    }

    return ticket
  }
}
